import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Spinner, Typography } from "@material-tailwind/react";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { PortalShell } from "../components/PortalShell";
import PsychologistAppointmentsCalendar, {
  defaultCalendarSelectedYmd,
  PsychologistCalendarLegend,
} from "../components/psychologist/PsychologistAppointmentsCalendar";
import { parseYmdAsLocalNoon } from "../utils/bookingWindowMx";

const POLL_MS = 15000;

export default function PortalPsychologistCalendar() {
  const { user, isAuthenticated } = usePortalAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYmd, setSelectedYmd] = useState(defaultCalendarSelectedYmd);
  const [displayMonth, setDisplayMonth] = useState(() =>
    parseYmdAsLocalNoon(defaultCalendarSelectedYmd())
  );

  const fetchAppointments = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await portalApi.psychologistAppointments();
      setRows(r.appointments || []);
    } catch {
      if (!silent) {
        toast.error("No se pudieron cargar las citas");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== "psychologist") return;
    fetchAppointments();
  }, [isAuthenticated, user?.role, fetchAppointments]);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== "psychologist") return;

    const poll = () => {
      if (document.visibilityState === "visible") {
        fetchAppointments(true);
      }
    };

    const intervalId = window.setInterval(poll, POLL_MS);
    const onFocus = () => fetchAppointments(true);
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchAppointments(true);
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isAuthenticated, user?.role, fetchAppointments]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "psychologist") {
    if (user?.role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/panel" replace />;
  }

  return (
    <PortalShell
      wide
      title="Calendario de citas"
      subtitle="Vista mensual de tus sesiones. Se actualiza automáticamente cuando hay cambios."
    >
      {!loading && <div className="mb-4"><PsychologistCalendarLegend /></div>}

      {loading ? (
        <div className="flex items-center gap-3 text-brand-gray py-16 justify-center">
          <Spinner className="h-8 w-8" />
          <span>Cargando calendario…</span>
        </div>
      ) : (
        <PsychologistAppointmentsCalendar
          appointments={rows}
          selectedYmd={selectedYmd}
          onSelectYmd={setSelectedYmd}
          displayMonth={displayMonth}
          onDisplayMonthChange={setDisplayMonth}
        />
      )}

      {!loading && rows.length === 0 && (
        <Typography className="mt-6 text-center text-sm text-brand-gray">
          Cuando un paciente reserve contigo, la cita aparecerá en el calendario.
        </Typography>
      )}
    </PortalShell>
  );
}
